/* Copyright 2016-2022
   Kabandy
   Author: Cedric Hoarau

   TODO: Refacto / Merge with ApiWeb/RequestHandler.ts
 */

import { ContentType } from "../Network";
import { ApiDataModel } from "../ApiDataModel";
import { ApiFetchHelper } from "../ApiWeb";

type IApiWorkspace = ApiDataModel.IWorkspace;
type IApiModel = ApiDataModel.IModel;
type IApiModelfile = ApiDataModel.IModelfile;
type IApiVersion = ApiDataModel.IVersion;
type IApiVersionStatus = ApiDataModel.IVersionStatus;

export namespace Network {
    //
    // --- Post - https://api.kabandy.com/v1/auth/token - response is an authentication token

    export async function PostAuthToken(
        apiUrl: string,
        apiKey: string,
        options: { userId?: string; companyId?: string; email?: string; contractId?: string }
    ): Promise<{ accessToken?: string; refreshToken?: string; expiresIn?: string, error?: any }> {
        const headers: any = _CreateApiRequestHeaders(apiKey);
        const bodyAsStr: string = JSON.stringify(options);
        const response: any = await ApiFetchHelper.Fetch("POST", apiUrl, headers, bodyAsStr);
        return response.json();
    }

    //
    // --- Post - https://api.kabandy.com/v1/auth/token/revoke - response is empty

    export async function PostAuthTokenRevoke(apiUrl: string, apiKey: string, accessToken: string): Promise<void> {
        const headers: any = _CreateApiRequestHeaders(apiKey, accessToken);
        const response: any = await ApiFetchHelper.Fetch("POST", apiUrl, headers);
        return response.json();
    }

    //
    // --- Post - https://accounts.kabandy.com/api/token - response is an authentication token

    export async function PostAccountsApiToken( apiUrl: string, options: { grantType: string, redirectUri: string, clientId: string, code: string, codeVerifier?: string, clientSecret?: string } ) : Promise<{ accessToken?: string; refreshToken?: string; expiresIn?: string, error?: any }> {
        const headers: any = options.clientSecret ? _CreateAccountsBasicAuthApiRequestHeaders( options.clientId, options.clientSecret ) : _CreateAccountsApiTokenRequestHeaders()
        
        let body = new URLSearchParams();

        body.append( "code", options.code );
        body.append( "grantType", options.grantType );
        body.append( "redirectUri", options.redirectUri );

        // PKCE Extension
        const codeVerifier : string = options.codeVerifier || "";
        if(codeVerifier !== "" ) {
            body.append( "clientId", options.clientId );
            body.append( "codeVerifier", codeVerifier );
        }

        const response: any = await ApiFetchHelper.Fetch("POST", apiUrl, headers, body);
        return response.json();
    }

    //
    // --- Get|Post|Put - https://api.kabandy.com/v1/@me/workspaces

    export async function GetMeWorkspaces(apiUrl: string, apiKey: string, accessToken: string): Promise<{ workspaces: IApiWorkspace[] }> {
        const headers: any = _CreateApiRequestHeaders(apiKey, accessToken);
        const response: any = await ApiFetchHelper.Fetch("GET", apiUrl, headers);
        return response.json();
    }
    export async function PostMeWorkspaces(apiUrl: string, apiKey: string, accessToken: string, name: string): Promise<{ workspace: IApiWorkspace }> {
        const headers: any = _CreateApiRequestHeaders(apiKey, accessToken);
        const bodyAsStr: string = JSON.stringify({ name: name });
        const response: any = await ApiFetchHelper.Fetch("POST", apiUrl, headers, bodyAsStr);
        return response.json();
    }
    export async function GetMeWorkspace(apiUrl: string, apiKey: string, accessToken: string): Promise<{ workspace: IApiWorkspace }> {
        const headers: any = _CreateApiRequestHeaders(apiKey, accessToken);
        const response: any = await ApiFetchHelper.Fetch("GET", apiUrl, headers);
        return response.json();
    }
    export async function PutMeWorkspace(apiUrl: string, apiKey: string, accessToken: string, name: string): Promise<{ workspace: IApiWorkspace }> {
        const headers: any = _CreateApiRequestHeaders(apiKey, accessToken);
        const bodyAsStr: string = JSON.stringify({ name: name });
        const response: any = await ApiFetchHelper.Fetch("PUT", apiUrl, headers, bodyAsStr);
        return response.json();
    }

    //
    // --- Get|Post|Put|Delete - https://api.kabandy.com/v1/@me/workspaces/:workspaceId/models

    export async function GetMeModels(apiUrl: string, apiKey: string, accessToken: string): Promise<{ models: IApiModel[] }> {
        const headers: any = _CreateApiRequestHeaders(apiKey, accessToken);
        const response: any = await ApiFetchHelper.Fetch("GET", apiUrl, headers);
        return response.json();
    }
    export async function PostMeModels(apiUrl: string, apiKey: string, accessToken: string, name: string): Promise<{ model: IApiModel }> {
        const headers: any = _CreateApiRequestHeaders(apiKey, accessToken);
        const bodyAsStr: string = JSON.stringify({ name: name });
        const response: any = await ApiFetchHelper.Fetch("POST", apiUrl, headers, bodyAsStr);
        return response.json();
    }
    export async function GetMeModel(apiUrl: string, apiKey: string, accessToken: string): Promise<{ model: IApiModel }> {
        const headers: any = _CreateApiRequestHeaders(apiKey, accessToken);
        const response: any = await ApiFetchHelper.Fetch("GET", apiUrl, headers);
        return response.json();
    }
    export async function PutMeModel(apiUrl: string, apiKey: string, accessToken: string, name: string): Promise<{ model: IApiModel }> {
        const headers: any = _CreateApiRequestHeaders(apiKey, accessToken);
        const bodyAsStr: string = JSON.stringify({ name: name });
        const response: any = await ApiFetchHelper.Fetch("PUT", apiUrl, headers, bodyAsStr);
        return response.json();
    }
    export async function DeleteMeModel(apiUrl: string, apiKey: string, accessToken: string): Promise<void> {
        const headers: any = _CreateApiRequestHeaders(apiKey, accessToken);
        const response: any = await ApiFetchHelper.Fetch("DELETE", apiUrl, headers);
        return response.json();
    }

    //
    // --- Get|Post|Delete - https://api.kabandy.com/v1/@me/workspaces/:workspaceId/models/:modelId/modelfiles

    export async function GetMeModelfiles(apiUrl: string, apiKey: string, accessToken: string): Promise<{ modelfiles: IApiModelfile[] }> {
        const headers: any = _CreateApiRequestHeaders(apiKey, accessToken);
        const response: any = await ApiFetchHelper.Fetch("GET", apiUrl, headers);
        return response.json();
    }
    export async function PostMeModelfiles(
        apiUrl: string,
        apiKey: string,
        accessToken: string,
        fileId: string
    ): Promise<{ modelfile: IApiModelfile }> {
        const headers: any = _CreateApiRequestHeaders(apiKey, accessToken);
        const bodyAsStr: string = JSON.stringify({ fileId: fileId });
        const response: any = await ApiFetchHelper.Fetch("POST", apiUrl, headers, bodyAsStr);
        return response.json();
    }
    export async function GetMeModelfile(apiUrl: string, apiKey: string, accessToken: string): Promise<{ modelfile: IApiModelfile }> {
        const headers: any = _CreateApiRequestHeaders(apiKey, accessToken);
        const response: any = await ApiFetchHelper.Fetch("GET", apiUrl, headers);
        return response.json();
    }
    export async function DeleteMeModelfile(apiUrl: string, apiKey: string, accessToken: string): Promise<void> {
        const headers: any = _CreateApiRequestHeaders(apiKey, accessToken);
        const response: any = await ApiFetchHelper.Fetch("DELETE", apiUrl, headers);
        return response.json();
    }

    //
    // --- Get|Post|Delete - https://api.kabandy.com/v1/@me/workspaces/:workspaceId/models/:modelId/modelfiles/:modelfileId/versions

    export async function GetMeVersions(apiUrl: string, apiKey: string, accessToken: string): Promise<{ versions: IApiVersion[] }> {
        const headers: any = _CreateApiRequestHeaders(apiKey, accessToken);
        const response: any = await ApiFetchHelper.Fetch("GET", apiUrl, headers);
        return response.json();
    }
    export async function PostMeVersions(apiUrl: string, apiKey: string, accessToken: string, fileId: string): Promise<{ version: IApiVersion }> {
        const headers: any = _CreateApiRequestHeaders(apiKey, accessToken);
        const bodyAsStr: string = JSON.stringify({ fileId: fileId });
        const response: any = await ApiFetchHelper.Fetch("POST", apiUrl, headers, bodyAsStr);
        return response.json();
    }
    export async function GetMeVersion(apiUrl: string, apiKey: string, accessToken: string): Promise<{ version: IApiVersion }> {
        const headers: any = _CreateApiRequestHeaders(apiKey, accessToken);
        const response: any = await ApiFetchHelper.Fetch("GET", apiUrl, headers);
        return response.json();
    }
    export async function DeleteMeVersion(apiUrl: string, apiKey: string, accessToken: string): Promise<void> {
        const headers: any = _CreateApiRequestHeaders(apiKey, accessToken);
        const response: any = await ApiFetchHelper.Fetch("DELETE", apiUrl, headers);
        return response.json();
    }

    //
    // --- Get - https://api.kabandy.com/v1/@me/workspaces/:workspaceId/models/:modelId/modelfiles/:modelfileId/version/:versionId/status

    export async function GetMeVersionStatus(apiUrl: string, apiKey: string, accessToken: string): Promise<{ status: IApiVersionStatus }> {
        const headers: any = _CreateApiRequestHeaders(apiKey, accessToken);
        const response: any = await ApiFetchHelper.Fetch("GET", apiUrl, headers);
        return response.json();
    }

    //
    // --- Generic

    function _CreateApiRequestHeaders(apiKey?: string, token?: string): any {
        let headers: any = {};

        headers["Content-Type"] = ContentType.Json;

        if( apiKey ) {
            headers["x-api-key"] = apiKey;
        }

        if (token) {
            headers["Authorization"] = "Bearer " + token;
        }

        return headers;
    }
    function _CreateAccountsApiTokenRequestHeaders() {
        let headers: any = {};

        headers["Content-Type"] = ContentType.FormUrlEncoded;

        return headers;
    }
    function _CreateAccountsBasicAuthApiRequestHeaders( clientId: string, clientSecret: string ) : any {
        let headers: any = {};

        headers[ "Content-Type"] = ContentType.FormUrlEncoded;

        const credentials : string = `${clientId}:${clientSecret}`;
        const credentialsBase64Str : string = btoa(credentials);
        headers["Authorization"] = "Basic " + credentialsBase64Str;

        return headers;
    }
}
